import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    IconButton,
    TablePagination,
    Tooltip,
    Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { format } from "date-fns";
import holidaysApi from "../../api/holidays";
import CreateHolidayDialog from "./CreateHolidayDialog";
import Layout from "../../components/layout/Layout";

const Holidays: React.FC = () => {
    const [holidays, setHolidays] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

    const fetchHolidays = async () => {
        setLoading(true);
        try {
            const response = await holidaysApi.getHolidays({ page: page + 1, limit: rowsPerPage });
            setHolidays(response.holidays);
            setTotal(response.pagination.total);
        } catch (error) {
            console.error("Failed to fetch holidays", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHolidays();
    }, [page, rowsPerPage]);

    const handleCreate = async (data: { date: Date; title: string; description: string }, shouldApply: boolean = false) => {
        try {
            const response = await holidaysApi.createHoliday(data);
            setMessage({ type: "success", text: "Holiday created successfully" });

            if (shouldApply && response.holiday && response.holiday._id) {
                await handleApply(response.holiday._id);
            } else {
                fetchHolidays();
            }
        } catch (error: any) {
            setMessage({ type: "error", text: error.response?.data?.message || "Failed to create holiday" });
        }
    };

    const handleApply = async (id: string) => {
        try {
            await holidaysApi.applyHoliday(id);
            setMessage({ type: "success", text: "Holiday application started. Status update may take time." });
            fetchHolidays();
        } catch (error: any) {
            setMessage({ type: "error", text: error.response?.data?.message || "Failed to apply holiday" });
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm("Are you sure you want to delete this holiday?")) {
            try {
                await holidaysApi.deleteHoliday(id);
                setMessage({ type: "success", text: "Holiday deleted successfully" });
                fetchHolidays();
            } catch (error: any) {
                setMessage({ type: "error", text: error.response?.data?.message || "Failed to delete holiday" });
            }
        }
    };

    const getStatusChip = (status: string) => {
        const map: any = {
            DRAFT: "default",
            APPLYING: "warning",
            APPLIED: "success",
            FAILED: "error",
        };
        return <Chip label={status} color={map[status] || "default"} size="small" />;
    };

    return (
        <Layout>
            <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                    <Typography variant="h4">Operations Holidays</Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setDialogOpen(true)}
                    >
                        Create Holiday
                    </Button>
                </Box>

                {message && (
                    <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
                        {message.text}
                    </Alert>
                )}

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="center">Affected Orders</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {holidays.map((holiday) => (
                                <TableRow key={holiday._id}>
                                    <TableCell>{format(new Date(holiday.date), "dd MMM yyyy")}</TableCell>
                                    <TableCell>
                                        <Typography variant="subtitle2">{holiday.title}</Typography>
                                        <Typography variant="caption" color="textSecondary">{holiday.description}</Typography>
                                    </TableCell>
                                    <TableCell>{getStatusChip(holiday.status)}</TableCell>
                                    <TableCell align="center">{holiday.stats?.ordersRemoved || "-"}</TableCell>
                                    <TableCell align="right">
                                        {(holiday.status === "DRAFT" || holiday.status === "FAILED") && (
                                            <Tooltip title="Apply Holiday">
                                                <IconButton color="primary" onClick={() => handleApply(holiday._id)}>
                                                    <PlayArrowIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {holiday.status === "DRAFT" && (
                                            <Tooltip title="Delete">
                                                <IconButton color="error" onClick={() => handleDelete(holiday._id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                    />
                </TableContainer>

                <CreateHolidayDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onSubmit={handleCreate}
                />
            </Box>
        </Layout>
    );
};

export default Holidays;
